import { Box, Container, Link, Stack, Typography, Chip, Button } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';
import { GEOVERA_WEB_URL } from '../config/api';
import { LogoIcon } from './Logo';
import DigipinDisclaimer from './site/DigipinDisclaimer';

const productLinks = [
  ['Open free map', '/map'],
  ['Pricing', '/pricing'],
  ['Account', '/account'],
  ['QGIS Plugins', '/qgis-plugins'],
] as const;

const solutionLinks = [
  ['DIGIPIN Encoder Pro', '/digipin-encoder'],
  ['ClipToCAD', '/cliptocad'],
  ['DIGIPIN Developer API', '/developers/digipin'],
  ['Enterprise inquiry', 'mailto:support@geovera.in?subject=GeoVera%20Enterprise'],
] as const;

const companyLinks = [
  ['Terms of Service', '/terms'],
  ['Privacy Policy', '/privacy'],
  ['Support', 'mailto:support@geovera.in'],
  ['GitHub', 'https://github.com/landvera/geovera'],
] as const;

interface FooterProps {
  /** When true, removes the large top margin used on marketing pages. */
  compact?: boolean;
}

const Footer = ({ compact = false }: FooterProps) => {
  const currentYear = new Date().getFullYear();
  const domain = GEOVERA_WEB_URL.replace(/^https?:\/\//, '');

  const linkSx = {
    color: '#94A3B8',
    fontSize: 13,
    fontWeight: 500,
    textDecoration: 'none',
    transition: 'color 0.2s ease',
    '&:hover': { color: '#60A5FA' },
  } as const;

  const colLabelSx = {
    color: '#D97706',
    fontFamily: 'monospace',
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.12em',
    mb: 2,
  } as const;

  const renderLink = (label: string, to: string) => {
    const external = to.startsWith('http') || to.startsWith('mailto:');
    if (external) {
      return (
        <Link
          key={to + label}
          href={to}
          target={to.startsWith('http') ? '_blank' : undefined}
          rel={to.startsWith('http') ? 'noopener noreferrer' : undefined}
          sx={linkSx}
        >
          {label}
        </Link>
      );
    }
    return (
      <Link key={to} component={RouterLink} to={to} sx={linkSx}>
        {label}
      </Link>
    );
  };

  return (
    <Box
      component="footer"
      sx={{
        mt: compact ? 0 : { xs: 6, md: 10 },
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        overflowX: 'hidden',
        borderTop: '1px solid rgba(219, 234, 254, 0.12)',
        bgcolor: '#080E1E',
        color: '#F8FAFC',
      }}
    >
      {/* Enterprise CTA strip */}
      {!compact && (
        <Box
          sx={{
            borderBottom: '1px solid rgba(219, 234, 254, 0.08)',
            bgcolor: 'rgba(15, 23, 42, 0.65)',
          }}
        >
          <Container maxWidth="lg" sx={{ py: { xs: 3.5, md: 4 } }}>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={2}
              alignItems={{ xs: 'flex-start', md: 'center' }}
              justifyContent="space-between"
            >
              <Box>
                <Typography
                  sx={{ fontWeight: 800, fontSize: { xs: 18, md: 20 }, letterSpacing: '-0.02em' }}
                >
                  Need Enterprise WebGIS for your organisation?
                </Typography>
                <Typography sx={{ color: '#94A3B8', fontSize: 14, mt: 0.5, maxWidth: 520 }}>
                  Design partners · bulk DIGIPIN · SLA conversation · SIFA Geolabs
                </Typography>
              </Box>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                <Button
                  component={RouterLink}
                  to="/pricing"
                  variant="contained"
                  endIcon={<ArrowOutwardIcon />}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 700,
                    bgcolor: '#D97706',
                    '&:hover': { bgcolor: '#B45309' },
                    whiteSpace: 'nowrap',
                  }}
                >
                  View Enterprise pricing
                </Button>
                <Button
                  href="mailto:support@geovera.in?subject=GeoVera%20Enterprise%20inquiry"
                  variant="outlined"
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    borderColor: 'rgba(219, 234, 254, 0.2)',
                    color: '#F8FAFC',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Contact sales
                </Button>
              </Stack>
            </Stack>
          </Container>
        </Box>
      )}

      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1.4fr 1fr 1fr 1fr' },
            gap: { xs: 4, md: 5 },
          }}
        >
          {/* Brand */}
          <Box>
            <Stack direction="row" spacing={1.25} sx={{ mb: 2, alignItems: 'center' }}>
              <Box
                sx={{
                  display: 'flex',
                  p: 0.75,
                  bgcolor: 'rgba(30, 58, 138, 0.3)',
                  border: '1px solid rgba(59, 130, 246, 0.3)',
                  borderRadius: 2,
                }}
              >
                <LogoIcon size={22} />
              </Box>
              <Typography sx={{ fontSize: 20, fontWeight: 800, letterSpacing: '-0.03em' }}>
                GeoVera
              </Typography>
              <Chip
                label="Enterprise WebGIS"
                size="small"
                sx={{
                  bgcolor: 'rgba(217, 119, 6, 0.15)',
                  color: '#F59E0B',
                  fontSize: 10,
                  fontWeight: 700,
                  height: 20,
                }}
              />
            </Stack>
            <Typography
              sx={{ maxWidth: 320, color: '#94A3B8', fontSize: 14, lineHeight: 1.65, mb: 2 }}
            >
              India-first spatial platform: free full-product WebGIS for humans, commercial API and
              organisation programmes via SIFA Geolabs.
            </Typography>
            <Typography sx={{ fontSize: 12, color: '#64748B', fontFamily: 'monospace' }}>
              asia-south1 · PostGIS · DIGIPIN
            </Typography>
          </Box>

          <Box>
            <Typography sx={colLabelSx}>PRODUCT</Typography>
            <Stack spacing={1.2}>{productLinks.map(([label, to]) => renderLink(label, to))}</Stack>
          </Box>

          <Box>
            <Typography sx={colLabelSx}>SOLUTIONS</Typography>
            <Stack spacing={1.2}>{solutionLinks.map(([label, to]) => renderLink(label, to))}</Stack>
          </Box>

          <Box>
            <Typography sx={colLabelSx}>COMPANY</Typography>
            <Stack spacing={1.2}>{companyLinks.map(([label, to]) => renderLink(label, to))}</Stack>
          </Box>
        </Box>

        <Box
          sx={{
            pt: 3,
            mt: 6,
            borderTop: '1px solid rgba(219, 234, 254, 0.08)',
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', sm: 'center' },
            gap: 2,
          }}
        >
          <Typography sx={{ color: '#64748B', fontFamily: 'monospace', fontSize: 11 }}>
            © {currentYear} GeoVera · Operated with SIFA Geolabs · {domain}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'row', gap: 3, flexWrap: 'wrap' }}>
            <Link component={RouterLink} to="/terms" sx={linkSx}>
              Terms
            </Link>
            <Link component={RouterLink} to="/privacy" sx={linkSx}>
              Privacy
            </Link>
            <Link href="mailto:support@geovera.in" sx={linkSx}>
              support@geovera.in
            </Link>
          </Box>
        </Box>

        <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid rgba(219, 234, 254, 0.06)' }}>
          <DigipinDisclaimer variant="footer" />
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;
